import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Wrench, Plus, Search, MoreHorizontal, Pencil, Trash2, Copy, Clock, Package, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { JOB_CATEGORY_VALUES, DEFAULT_JOB_CATEGORY } from '@/constants/jobCategories';

const CATEGORIES = ['all', ...JOB_CATEGORY_VALUES];
const JOB_TYPES = ['drilling', 'pump', 'service', 'other'];
const CATEGORY_COLORS = {
  well_construction: 'bg-blue-100 text-blue-700',
  pump_installer: 'bg-emerald-100 text-emerald-700',
  filters_service: 'bg-amber-100 text-amber-700',
  permits: 'bg-indigo-100 text-indigo-700',
  abandonment: 'bg-slate-100 text-slate-700'
};

export default function JobTemplatesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ 
    name: '', 
    description: '', 
    category: DEFAULT_JOB_CATEGORY, 
    job_type: 'drilling',
    estimated_duration_hours: 8,
    notes: '', 
    is_active: true, 
    default_items: [{ name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }],
    checklist: []
  });

  const fetchData = useCallback(async () => {
    try {
      const params = { active_only: false };
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      const res = await api.get('/job-templates', { params });
      let filtered = res.data;
      if (search) {
        const s = search.toLowerCase();
        filtered = filtered.filter(tp => tp.name?.toLowerCase().includes(s) || tp.description?.toLowerCase().includes(s));
      }
      setTemplates(filtered);
    } catch { toast.error(t('jobTemplates.errorLoading')); }
    finally { setLoading(false); }
  }, [categoryFilter, search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ 
      name: '', 
      description: '', 
      category: DEFAULT_JOB_CATEGORY, 
      job_type: 'drilling',
      estimated_duration_hours: 8,
      notes: '', 
      is_active: true, 
      default_items: [{ name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }],
      checklist: []
    });
    setDialogOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({
      name: template.name || '', 
      description: template.description || '', 
      category: template.category || DEFAULT_JOB_CATEGORY,
      job_type: template.job_type || 'drilling',
      estimated_duration_hours: template.estimated_duration_hours || 8,
      notes: template.notes || '', 
      is_active: template.is_active !== false,
      default_items: template.default_items?.length 
        ? template.default_items.map(i => ({ name: i.name || '', description: i.description || '', quantity: i.quantity || 1, unit: i.unit || 'unit', unit_price: i.unit_price || 0 }))
        : [{ name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }],
      checklist: template.checklist || []
    });
    setDialogOpen(true);
  };

  const duplicateTemplate = (template) => {
    setEditing(null);
    setForm({
      name: `${template.name} (Copia)`, 
      description: template.description || '', 
      category: template.category || DEFAULT_JOB_CATEGORY,
      job_type: template.job_type || 'drilling',
      estimated_duration_hours: template.estimated_duration_hours || 8,
      notes: template.notes || '', 
      is_active: true,
      default_items: template.default_items?.length 
        ? template.default_items.map(i => ({ name: i.name || '', description: i.description || '', quantity: i.quantity || 1, unit: i.unit || 'unit', unit_price: i.unit_price || 0 }))
        : [{ name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }],
      checklist: template.checklist || []
    });
    setDialogOpen(true);
    toast.info(t('jobTemplates.duplicating'));
  };

  const addItem = () => setForm({ ...form, default_items: [...form.default_items, { name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }] });
  const removeItem = (idx) => setForm({ ...form, default_items: form.default_items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, val) => {
    const items = [...form.default_items];
    items[idx] = { ...items[idx], [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(val) || 0 : val };
    setForm({ ...form, default_items: items });
  };
  const calcTotal = () => form.default_items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0);

  const addChecklistItem = () => setForm({ ...form, checklist: [...form.checklist, { text: '', required: false }] });
  const removeChecklistItem = (idx) => setForm({ ...form, checklist: form.checklist.filter((_, i) => i !== idx) });
  const updateChecklistItem = (idx, field, val) => {
    const items = [...form.checklist];
    items[idx] = { ...items[idx], [field]: val };
    setForm({ ...form, checklist: items });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('errors.requiredField')); return; }
    try {
      let saved;
      if (editing) {
        const res = await api.put(`/job-templates/${editing.id}`, form);
        saved = res.data;
        toast.success(t('success.updated'));
      } else {
        const res = await api.post('/job-templates', form);
        saved = res.data;
        toast.success(t('success.created'));
      }
      // Save keeps the dialog open (Alian: "no quiero estar abriendo y
      // cerrando... quiero dejar la plantilla abierta"). Switch a brand-new
      // template into edit mode so a second Save does PUT, not another POST.
      setEditing(saved);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      await api.delete(`/job-templates/${template.id}`);
      toast.success(t('success.deleted'));
      fetchData();
    } catch { toast.error(t('errors.deletingError')); }
  };

  // Create job from template
  const createJobFromTemplate = (template) => {
    // Navigate to jobs page with template data
    navigate('/jobs', { state: { fromTemplate: template } });
    toast.info(t('jobTemplates.usingTemplate', { name: template.name }));
  };

  const filtered = templates;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Barlow']">{t('jobTemplates.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('jobTemplates.totalTemplates', { count: filtered.length })}</p>
        </div>
        <Button data-testid="new-job-template-btn" onClick={openCreate} className="bg-[#003366] hover:bg-[#004080] text-white">
          <Plus className="w-4 h-4 mr-2" /> {t('jobTemplates.newTemplate')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder={t('common.search') + '...'} value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="w-full sm:w-auto">
          <TabsList className="h-10 bg-white border border-gray-200">
            {CATEGORIES.map(cat => (
              <TabsTrigger key={cat} value={cat} className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white">
                {t(`jobTemplates.categories.${cat}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-40 bg-gray-100 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border border-gray-200 bg-gray-50">
          <CardContent className="py-12 text-center">
            <Wrench className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">{t('jobTemplates.noTemplates')}</p>
            <Button variant="outline" className="mt-4" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" /> {t('jobTemplates.createFirstTemplate')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(template => (
            <Card key={template.id} className={`border border-gray-200 hover:shadow-md transition-all cursor-pointer group ${!template.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[template.category] || 'bg-gray-100 text-gray-700'}`}>
                        {t(`jobTemplates.categories.${template.category}`)}
                      </span>
                      {!template.is_active && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                          {t('common.inactive')}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                    {template.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">{template.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {template.estimated_duration_hours || 8}h
                      </span>
                      {template.default_items?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Package className="w-3.5 h-3.5" />
                          {template.default_items.length} {t('common.items')}
                        </span>
                      )}
                      {template.checklist?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <CheckSquare className="w-3.5 h-3.5" />
                          {template.checklist.length}
                        </span>
                      )}
                    </div>
                    {template.estimated_cost > 0 && (
                      <p className="text-sm font-semibold text-emerald-600 mt-2">${template.estimated_cost.toFixed(2)}</p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => createJobFromTemplate(template)}>
                        <Wrench className="w-4 h-4 mr-2" /> {t('jobTemplates.useTemplate')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(template)}>
                        <Pencil className="w-4 h-4 mr-2" /> {t('common.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicateTemplate(template)}>
                        <Copy className="w-4 h-4 mr-2" /> {t('common.duplicate')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(template)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> {t('common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {/* Quick action button */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-4 border-dashed"
                  onClick={() => createJobFromTemplate(template)}
                >
                  <Wrench className="w-3.5 h-3.5 mr-2" />
                  {t('jobTemplates.createJob')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('jobTemplates.editTemplate') : t('jobTemplates.newTemplate')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('jobTemplates.templateName')} *</Label>
                <Input 
                  data-testid="template-name-input" 
                  value={form.name} 
                  onChange={e => setForm({ ...form, name: e.target.value })} 
                  placeholder={t('jobTemplates.templateNamePlaceholder')} 
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.category')}</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c !== 'all').map(cat => (
                      <SelectItem key={cat} value={cat}>{t(`jobTemplates.categories.${cat}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('jobTemplates.jobType')}</Label>
                <Select value={form.job_type} onValueChange={v => setForm({ ...form, job_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JOB_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{t(`jobTemplates.jobTypes.${type}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('jobTemplates.estimatedDuration')}</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number" 
                    value={form.estimated_duration_hours} 
                    onChange={e => setForm({ ...form, estimated_duration_hours: parseFloat(e.target.value) || 8 })} 
                    min="0.5"
                    step="0.5"
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">{t('common.hours')}</span>
                </div>
              </div>
              <div className="space-y-2 flex items-center gap-3">
                <Switch 
                  id="is_active" 
                  checked={form.is_active} 
                  onCheckedChange={v => setForm({ ...form, is_active: v })} 
                />
                <Label htmlFor="is_active" className="cursor-pointer">{t('common.active')}</Label>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('common.description')}</Label>
                <Textarea 
                  value={form.description} 
                  onChange={e => setForm({ ...form, description: e.target.value })} 
                  placeholder={t('jobTemplates.descriptionPlaceholder')} 
                  rows={2} 
                />
              </div>
            </div>

            {/* Default Line Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-700">{t('jobTemplates.defaultItems')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-3 h-3 mr-1" /> {t('common.addItem')}
                </Button>
              </div>
              <div className="space-y-2">
                {form.default_items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-start p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <Input 
                      value={item.name} 
                      onChange={e => updateItem(idx, 'name', e.target.value)} 
                      placeholder={t('common.name')} 
                      className="flex-1 bg-white h-9" 
                    />
                    <Input 
                      type="number" 
                      value={item.quantity} 
                      onChange={e => updateItem(idx, 'quantity', e.target.value)} 
                      className="w-20 bg-white h-9" 
                      min="0" 
                    />
                    <Input 
                      type="number" 
                      value={item.unit_price} 
                      onChange={e => updateItem(idx, 'unit_price', e.target.value)} 
                      placeholder={t('common.price')} 
                      className="w-24 bg-white h-9" 
                      min="0" 
                      step="0.01" 
                    />
                    <span className="text-sm font-medium text-gray-600 w-20 text-right py-2">
                      ${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}
                    </span>
                    {form.default_items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-9 w-9 text-red-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end px-3">
                <div className="text-right">
                  <span className="text-sm text-gray-500">{t('common.total')}: </span>
                  <span className="text-lg font-bold text-gray-900">${calcTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Checklist */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-700">{t('jobTemplates.checklist')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addChecklistItem}>
                  <Plus className="w-3 h-3 mr-1" /> {t('jobTemplates.addChecklistItem')}
                </Button>
              </div>
              {form.checklist.length > 0 && (
                <div className="space-y-2">
                  {form.checklist.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                      <CheckSquare className="w-4 h-4 text-gray-400" />
                      <Input 
                        value={item.text} 
                        onChange={e => updateChecklistItem(idx, 'text', e.target.value)} 
                        placeholder={t('jobTemplates.checklistItemPlaceholder')} 
                        className="flex-1 bg-white h-9" 
                      />
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={item.required} 
                          onCheckedChange={v => updateChecklistItem(idx, 'required', v)} 
                        />
                        <span className="text-xs text-gray-500">{t('common.required')}</span>
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeChecklistItem(idx)} className="h-8 w-8 text-red-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('common.notes')}</Label>
              <Textarea 
                value={form.notes} 
                onChange={e => setForm({ ...form, notes: e.target.value })} 
                placeholder={t('common.notes') + '...'} 
                rows={2} 
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-template-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('common.saveChanges') : t('jobTemplates.createTemplate')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
